import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-[150] space-y-1 w-full max-w-xs">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
};

const ToastItem = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  const getToastConfig = (type) => {
    const configs = {
      success: {
        icon: CheckCircle,
        bgColor: 'bg-white/95',
        borderColor: 'border-green-200/50',
        iconColor: 'text-green-500',
        textColor: 'text-gray-700',
        titleColor: 'text-gray-800'
      },
      error: {
        icon: AlertCircle,
        bgColor: 'bg-white/95',
        borderColor: 'border-red-200/50',
        iconColor: 'text-red-500',
        textColor: 'text-gray-700',
        titleColor: 'text-gray-800'
      },
      warning: {
        icon: AlertTriangle,
        bgColor: 'bg-white/95',
        borderColor: 'border-yellow-200/50',
        iconColor: 'text-yellow-500',
        textColor: 'text-gray-700',
        titleColor: 'text-gray-800'
      },
      info: {
        icon: Info,
        bgColor: 'bg-white/95',
        borderColor: 'border-blue-200/50',
        iconColor: 'text-blue-500',
        textColor: 'text-gray-700',
        titleColor: 'text-gray-800'
      },
      loading: {
        icon: Loader2,
        bgColor: 'bg-white/95',
        borderColor: 'border-gray-200/50',
        iconColor: 'text-gray-500',
        textColor: 'text-gray-700',
        titleColor: 'text-gray-800'
      }
    };
    return configs[type] || configs.info;
  };

  const config = getToastConfig(toast.type);
  const Icon = config.icon;

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${config.bgColor} ${config.borderColor} border rounded-lg shadow-sm p-3
        backdrop-blur-sm
      `}
    >
      <div className="flex items-start space-x-2">
        <div className="flex-shrink-0">
          {toast.type === 'loading' ? (
            <Loader2 className={`w-4 h-4 ${config.iconColor} animate-spin`} />
          ) : (
            <Icon className={`w-4 h-4 ${config.iconColor}`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {toast.title && (
            <h4 className={`text-sm font-semibold ${config.titleColor} mb-1`}>
              {toast.title}
            </h4>
          )}
          <p className={`text-sm ${config.textColor}`}>
            {toast.message}
          </p>
        </div>
        
        <button
          onClick={handleRemove}
          className="flex-shrink-0 ml-1 p-0.5 rounded-full hover:bg-black/5 transition-colors"
        >
          <X className="w-3 h-3 text-gray-400 hover:text-gray-600" />
        </button>
      </div>
    </div>
  );
};

export default ToastContainer;
